/**
 * trackReadiness — track setup progress + active-loop selection for
 * TrackWorkspace (desktop and phone).
 *
 * Readiness is advisory progress (conditions-met model), never a hard gate
 * on sessions or on sending the ball.
 */

import type { Track } from '@/types/prioritization/track';
import type { ItemPool } from '@/types/prioritization';
import {
  normalizeLoopState,
  type CatchballLoop,
} from '@/types/prioritization/catchballLoop';

export interface TrackReadiness {
  participationLocked: boolean;
  poolLocked: boolean;
  criteriaLocked: boolean;
  valuesConverged: boolean;
  hasSessions: boolean;
  isPublished: boolean;
}

export function computeTrackReadiness(
  track: Track,
  pools: ItemPool[],
  sessionCount: number,
): TrackReadiness {
  const trackPool = pools.find((p) => p.trackId === track.id);
  const poolLockedAt =
    (trackPool as Record<string, unknown> | undefined)?.poolLockedAt ??
    (trackPool as Record<string, unknown> | undefined)?.pool_locked_at;
  return {
    participationLocked: !!track.participationLockedAt,
    poolLocked: !!poolLockedAt,
    criteriaLocked: !!track.criteriaLockedAt,
    valuesConverged: !!track.valuesConvergedAt,
    hasSessions: sessionCount > 0,
    isPublished: track.status === 'accepted',
  };
}

export function trackConditionsMet(r: TrackReadiness): number {
  return [r.participationLocked, r.poolLocked, r.criteriaLocked, r.valuesConverged].filter(Boolean)
    .length;
}

/** Progress-only: how many upstream defaults are set. Never a hard gate on sessions. */
export function allTrackDesignConditionsMet(r: TrackReadiness): boolean {
  return r.participationLocked && r.poolLocked && r.criteriaLocked && r.valuesConverged;
}

/**
 * The track's live loop: non-closed, non-declined, highest loop number.
 * Same selection TrackWorkspace uses for the ball-holder banner and the
 * send-the-ball CTA.
 */
export function selectActiveTrackLoop(
  loops: CatchballLoop[],
  trackId: string | null | undefined,
): CatchballLoop | null {
  if (!trackId) return null;
  const live = loops
    .filter(
      (l) =>
        l.trackId === trackId &&
        normalizeLoopState(l.state) !== 'DECLINED' &&
        // DB rows can carry legacy casings outside the CatchballLoopState union.
        String(l.state).toLowerCase() !== 'closed',
    )
    .sort((a, b) => a.loopNumber - b.loopNumber);
  return live.length > 0 ? live[live.length - 1]! : null;
}
